import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Menu,
  User,
  LogOut,
  LayoutDashboardIcon,
} from "lucide-react";
import { cn } from "../../utils/cn";
import { useSelector, useDispatch } from "react-redux";
import { toggle } from "../../store/slices/sidebarSlice";
import { motion } from "framer-motion";
import { logout } from "@/store/slices/authSlice";
import { useAuth } from "@/hooks/useAuth";

import ProfileAvatar from "../common/ProfileAvatar";

export interface HeaderProps {
  onSidebarToggle?: () => void;
  showSidebar?: boolean;
  className?: string;
}

const Header: React.FC<HeaderProps> = ({
  onSidebarToggle,
  showSidebar = true,
  className,
}) => {
  const location = useLocation();
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isNotificationsOpen, setIsNotificationsOpen] = useState(false);
  const dispatch = useDispatch();
  const collapsed = useSelector((state: any) => state.sidebar.collapsed);
  const { user } = useAuth();

  const notifications = [
    {
      id: 1,
      title: "New customer registered",
      message: "John Doe has been added to your customer list",
      time: "2 minutes ago",
      unread: true,
    },
    {
      id: 2,
      title: "Deal closed",
      message: "Project Alpha has been successfully closed",
      time: "1 hour ago",
      unread: true,
    },
    {
      id: 3,
      title: "Task reminder",
      message: "Follow up with client scheduled for tomorrow",
      time: "3 hours ago",
      unread: false,
    },
  ];

  const handleSidebarToggle = () => {
    if (onSidebarToggle) {
      onSidebarToggle();
    } else {
      dispatch(toggle());
    }
  };
  // const dispatch = useDispatch();
  const navigate = useNavigate();

  const handleSignOutClick = async () => {
    await dispatch(logout() as any).unwrap();
    // navigate('/login', { replace: true });
  };

  const userMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!userMenuRef.current) return;
    function getScrollParents(el: Element | null) {
      const parents: (Window | Element)[] = [];
      let parent = el?.parentElement;
      while (parent) {
        const style = window.getComputedStyle(parent);
        const overflowY = style.overflowY;
        if (/(auto|scroll|overlay)/.test(overflowY)) {
          parents.push(parent);
        }
        parent = parent.parentElement;
      }
      parents.push(document);
      parents.push(window);
      return parents;
    }

    const handler = () => {
      setIsUserMenuOpen((v) => (v ? false : v));
    };

    const events: Array<keyof GlobalEventHandlersEventMap> = [
      "scroll",
      "wheel"
    ];

    const targets = getScrollParents(userMenuRef.current);

    targets.forEach((t) => {
      events.forEach((ev) => {
        try {
          (t as any).addEventListener(ev, handler, { capture: true });
        } catch (err) {
          (t as any).addEventListener(ev, handler as any);
        }
      });
    });

    return () => {
      targets.forEach((t) => {
        events.forEach((ev) => {
          try {
            (t as any).removeEventListener(ev, handler, { capture: true });
          } catch {
            (t as any).removeEventListener(ev, handler as any);
          }
        });
      });
    };
  }, [userMenuRef, setIsUserMenuOpen]);

  useEffect(() => {
    function handleOutsideClick(e: MouseEvent) {
      if (
        userMenuRef.current &&
        !userMenuRef.current.contains(e.target as Node)
      ) {
        setIsUserMenuOpen(false);
      }
    }

    document.addEventListener("click", handleOutsideClick);
    return () => document.removeEventListener("click", handleOutsideClick);
  }, []);

  return (
    <header
      className={cn(
        "bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700 shadow-sm sticky top-0 z-[100] transition-all duration-300 ease-in-out",
        className,
      )}
    >
      <div className="flex items-center justify-between px-4 py-3 lg:px-6">
        {/* Left Section */}
        <div className="flex items-center space-x-4">
          {/* Sidebar Toggle */}
          {showSidebar && (
            <button
              onClick={handleSidebarToggle}
              // className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-md sidebar-toggle-button transition-colors"
            >
              {window.innerWidth >= 1024 ? (
                <motion.div
                  initial={false}
                  // animate={{ rotate: collapsed ? 0 : 180 }}
                  // transition={{ duration: 0.3, ease: 'easeInOut' }}
                >
                  {/* <ChevronRight size={20} className="text-gray-600 dark:text-gray-300 sidebar-icon" /> */}
                  <LayoutDashboardIcon
                    size={22}
                    className="text-gray-600 dark:text-gray-300 sidebar-icon"
                  />
                </motion.div>
              ) : (
                <Menu
                  size={20}
                  className="text-gray-600 dark:text-gray-300 sidebar-icon"
                />
              )}
            </button>
          )}

          {/* Breadcrumbs */}
          {/* <div className="hidden md:block">
            <Breadcrumbs />
          </div> */}
        </div>

        {/* Center Section - Page Title for Mobile */}
        <div className="flex-1 ml-2 md:hidden">
          <h1 className="text-lg font-semibold text-gray-900 dark:text-gray-100 truncate">
            {location.pathname === "/app"
              ? "Dashboard"
              : (location.pathname.split("/").pop()?.charAt(0).toUpperCase() ||
                  "") + (location.pathname.split("/").pop()?.slice(1) || "")}
          </h1>
        </div>

        {/* Right Section */}
        <div className="flex items-center space-x-3">
          {/* Search */}
          {/* <div className="hidden lg:block">
            <SearchInput
              placeholder="Search here..."
              className="w-80"
              onSearch={(value) =>  }
            />
          </div> */}

          {/* Notifications */}
          <div className="relative">
            {/* <button
              onClick={() => setIsNotificationsOpen(!isNotificationsOpen)}
              className="relative p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-md transition-colors border"
            >
              <Bell size={20} className="text-gray-600 dark:text-gray-300" />
              {unreadCount > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                  {unreadCount}
                </span>
              )}
            </button> */}

            {/* Notifications Dropdown */}
            {isNotificationsOpen && (
              <div className="absolute right-0 mt-2 w-80 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md shadow-lg z-50">
                <div className="p-4 border-b border-gray-200 dark:border-gray-700">
                  <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100">
                    Notifications
                  </h3>
                </div>
                <div className="max-h-64 overflow-y-auto">
                  {notifications.map((notification) => (
                    <div
                      key={notification.id}
                      className={cn(
                        "p-4 border-b border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer transition-colors",
                        notification.unread && "bg-blue-50 dark:bg-blue-900/20",
                      )}
                    >
                      <div className="flex items-start space-x-3">
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                            {notification.title}
                          </p>
                          <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
                            {notification.message}
                          </p>
                          <p className="text-xs text-gray-400 dark:text-gray-500 mt-2">
                            {notification.time}
                          </p>
                        </div>
                        {notification.unread && (
                          <div className="w-2 h-2 bg-blue-500 rounded-full flex-shrink-0 mt-2" />
                        )}
                      </div>
                    </div>
                  ))}
                </div>
                <div className="p-4 border-t border-gray-200 dark:border-gray-700">
                  <button className="text-sm text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 font-medium transition-colors">
                    View all notifications
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Theme Toggle */}
          {/* <ThemeToggle 
            className="hidden sm:flex border"
            showLabels={false}
          /> */}

          {/* User Menu */}
          <div className="relative" ref={userMenuRef}>
            <button
              onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
              className="flex items-center space-x-2 p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-md transition-colors"
            >
              {/* <img src={avatar} alt="" width={35} /> */}
              <ProfileAvatar user={user} />
            </button>

            {isUserMenuOpen && (
              <div className="absolute right-1 top-8 mt-2 w-48 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md shadow-lg z-50">
                <div className="py-1">
                  <button
                    onClick={() => {
                      setIsUserMenuOpen(false);
                      navigate("/profile");
                    }}
                    className="flex items-center w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                  >
                    <User size={16} className="mr-3" />
                    Profile
                  </button>

                  <hr className="my-1 border-gray-200 dark:border-gray-700" />

                  <button
                    onClick={handleSignOutClick}
                    className="flex items-center w-full px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                  >
                    <LogOut size={16} className="mr-3" />
                    Sign out
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Mobile Breadcrumbs */}
      {/* <div className="md:hidden px-4 pb-3">
        <Breadcrumbs />
      </div> */}
    </header>
  );
};

export default Header;
